import React from 'react';
import { makeStyles } from '@mui/styles';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment-timezone';
import IMask from 'imask';
import { clsx } from 'clsx';

const { RangePicker } = DatePicker;
const DATE_FORMAT = 'DD/MM/YYYY';
const MASKED = IMask.createMask({
  blocks: {
    DD: { from: 1, mask: IMask.MaskedRange, to: 31 },
    MM: { from: 1, mask: IMask.MaskedRange, to: 12 },
    YYYY: { from: 1900, mask: IMask.MaskedRange, to: new Date().getFullYear() },
  },
  format: (date: Date) => moment(date).format(DATE_FORMAT),
  mask: Date,
  parse: (date: string) => moment(date, DATE_FORMAT),
  pattern: DATE_FORMAT,
});

interface Props {
  range: string;
  setRange: (range: string) => void;
  label?: string;
}

export const DateRangeControlledInput = ({ range, setRange, label }: Props) => {
  const cls = useStyles();
  const [start, end] = (range || '').split('-');

  const handleChange = (range: any) => {
    const dateStart: Moment = range && range[0];
    const dateEnd: Moment = range && range[1];
    const nextValues: string[] = [];
    if (dateStart) {
      nextValues.push(dateStart.format('DD.MM.YYYY'));
    }
    if (dateEnd) {
      nextValues.push(dateEnd.format('DD.MM.YYYY'));
    }
    const nextRange = nextValues.join('-');
    setRange(nextRange);
  };

  return (
    <div className={cls.wrap}>
      {label && <label className={cls.label}><span>{label}</span></label>}
      <div className={clsx('MuiInputBase-root', cls.box)}>
        <RangePicker
          onChange={handleChange}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            const input = event.target as HTMLInputElement;
            input.value = MASKED.resolve(input.value);
          }}
          value={[
            start ? moment(start, DATE_FORMAT) : null,
            end ? moment(end, DATE_FORMAT) : null,
          ]}
          defaultValue={[
            start ? moment(start, DATE_FORMAT) : null,
            end ? moment(end, DATE_FORMAT) : null,
          ]}
          format={DATE_FORMAT}
          placeholder={['dd/mm/yyyy', 'dd/mm/yyyy']}
          allowEmpty={[true, true]}
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  wrap: {
    width: 290,
    '& > div': {
      borderRadius: 8,
      border: '1px solid var(--logo-color)',
      background: 'var(--foreground-color)',
      display: 'flex',
    },
  },
  label: {
    display: 'block',
    color: 'var(--title-color)',
    fontSize: 12,
    lineHeight: 1,
    marginLeft: 14,
    marginBottom: 3,
  },
  box: {
    backgroundColor: 'var(--foreground-color)',
    overflow: 'hidden',
    padding: '8px 12px 8px 8px',

    '& .ant-picker-range': {
      border: 'none',
      boxShadow: 'none',
      backgroundColor: 'transparent !important',
    },

    '& .ant-picker-separator': {
      fontSize: 18,
      color: 'var(--primary-color)',
    },

    '& .ant-picker-suffix': {
      fontSize: 16,
      color: 'var(--primary-color)',
    },

    '& .ant-picker-input': {
      border: 'none !important',
    },

    '& .ant-picker-input > input': {
      border: 'none !important',
      padding: '7px !important',
      color: 'var(--text-color) !important',
      backgroundColor: 'transparent !important',
    },

    '& .ant-picker-input > input::placeholder': {
      color: 'var(--mutted-color)',
      fontFamily: 'var(--font-primary)',
      fontSize: 15,
      fontWeight: 400,
      lineHeight: 'normal',
    },

    '& .ant-picker-range .ant-picker-active-bar': {
      height: 1,
      width: '75px !important',
      backgroundColor: 'var(--primary-color)',
      bottom: -2,
      marginLeft: 7,
    },

    '& .ant-picker-range .ant-picker-clear': {
      right: -3,
      fontSize: 23,
      backgroundColor: 'var(--foreground-color) !important',
      color: 'var(--primary-color) !important',
    },
  },
});
